import React, { useState } from 'react';
import { ArrowLeft, User, Mail, Lock, Eye, EyeOff, AlertCircle, Phone, MapPin, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { theme } from '../styles/theme';

function RegisterPage({ onNavigate }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    clinicName: '',
    address: '',
    city: '',
    postalCode: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
    userType: 'client' // Par défaut client
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [registered, setRegistered] = useState(false);

  const { register, isLoading, error: authError, clearError } = useAuth();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Effacer l'erreur d'authentification si l'utilisateur modifie le formulaire
    if (authError) {
      clearError();
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Personal info validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Le prénom est requis';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Le nom est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'adresse email est requise';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Veuillez entrer une adresse email valide';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Le numéro de téléphone est requis';
    } else if (!/^(?:\+33|0)[1-9](?:[0-9]{8})$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Veuillez entrer un numéro de téléphone français valide';
    }

    // Clinic info validation (optional)
    // clinicName is now optional

    if (!formData.address.trim()) {
      newErrors.address = 'L\'adresse est requise';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'La ville est requise';
    }

    if (!formData.postalCode.trim()) {
      newErrors.postalCode = 'Le code postal est requis';
    } else if (!/^\d{5}$/.test(formData.postalCode)) {
      newErrors.postalCode = 'Le code postal doit contenir 5 chiffres';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Le mot de passe doit contenir au moins une minuscule, une majuscule et un chiffre';
    } else if (!/\W/.test(formData.password)) {
      newErrors.password = 'Le mot de passe doit contenir au moins un caractère spécial (!@#$%^&*...)';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Veuillez confirmer le mot de passe';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'Vous devez accepter les conditions d\'utilisation';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    const userData = {
      name: `${formData.firstName} ${formData.lastName}`,
      email: formData.email,
      password: formData.password,
      userType: formData.userType,
      clinicName: formData.clinicName,
      phone: formData.phone,
      address: `${formData.address}, ${formData.postalCode} ${formData.city}`
    };
    
    const result = await register(userData);

    if (result.success) {
      setRegistered(true);
    }
    // Les erreurs sont gérées par le contexte AuthContext
  };

  if (registered) {
    return (
      <div className={theme.classes.page + " flex items-center justify-center p-4"}>
        <div className="max-w-md w-full text-center">
          <div className={theme.classes.card.default + " shadow-xl p-8"}>
            <div className="bg-green-500/20 p-4 rounded-full w-fit mx-auto mb-4">
              <CheckCircle className="h-12 w-12 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">
              Vérifiez votre boîte mail
            </h1>
            <p className="text-gray-400 mb-6">
              Un email d'activation vient de vous être envoyé. Cliquez sur le lien qu'il contient
              pour activer votre compte, puis connectez-vous.
            </p>
            <button
              onClick={() => onNavigate('login')}
              className="px-6 py-2 bg-yellow-500 text-black rounded-lg
                         hover:bg-yellow-400 transition-colors font-medium"
            >
              Aller à la connexion
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={theme.classes.page + " py-12 px-4"}>
      <div className="max-w-2xl mx-auto">
        <div className={theme.classes.card.default + " shadow-xl p-8"}>
          {/* Header */}
          <div className="text-center mb-8">
            <button
              onClick={() => onNavigate('landing')}
              className={theme.classes.button.ghost + " mb-6 inline-flex items-center"}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à l'accueil
            </button>
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{
                backgroundColor: `${theme.colors.primary.DEFAULT}33`,
                borderWidth: '1px',
                borderColor: `${theme.colors.primary.DEFAULT}4D`
              }}
            >
              <span className="font-bold text-xl" style={{ color: theme.colors.primary.DEFAULT }}>S</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Créer un compte
            </h1>
            <p className="text-gray-400">
              Rejoignez ShinySmile et optimisez vos échanges
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {(errors.submit || authError) && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center space-x-2 text-red-400">
                <AlertCircle className="w-5 h-5" />
                <span className="text-sm">{errors.submit || authError}</span>
              </div>
            )}

            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white border-b border-white/[0.07] pb-2">
                Informations personnelles
              </h3>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Prénom *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 bg-black/30 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-transparent transition-all ${
                        errors.firstName ? 'border-red-500/50 bg-red-500/5' : 'border-white/[0.07]'
                      }`}
                      placeholder="Votre prénom"
                    />
                  </div>
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-400">{errors.firstName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nom *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 bg-black/30 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-transparent transition-all ${
                        errors.lastName ? 'border-red-500/50 bg-red-500/5' : 'border-white/[0.07]'
                      }`}
                      placeholder="Votre nom"
                    />
                  </div>
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-400">{errors.lastName}</p>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 bg-black/30 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-transparent transition-all ${
                        errors.email ? 'border-red-500/50 bg-red-500/5' : 'border-white/[0.07]'
                      }`}
                      placeholder="votre@email.fr"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Téléphone *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 bg-black/30 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-transparent transition-all ${
                        errors.phone ? 'border-red-500/50 bg-red-500/5' : 'border-white/[0.07]'
                      }`}
                      placeholder="01 23 45 67 89"
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-400">{errors.phone}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Clinic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white border-b border-white/[0.07] pb-2">
                Informations de votre organisation
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nom de l'organisation (optionnel)
                </label>
                <input
                  type="text"
                  name="clinicName"
                  value={formData.clinicName}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-black/30 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-transparent transition-all ${
                    errors.clinicName ? 'border-red-500/50 bg-red-500/5' : 'border-white/[0.07]'
                  }`}
                  placeholder="Cabinet dentaire Dr. Martin, Laboratoire Prothèse Plus, etc."
                />
                {errors.clinicName && (
                  <p className="mt-1 text-sm text-red-400">{errors.clinicName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Adresse *
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 bg-black/30 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-transparent transition-all ${
                      errors.address ? 'border-red-500/50 bg-red-500/5' : 'border-white/[0.07]'
                    }`}
                    placeholder="123 Rue de la Paix"
                  />
                </div>
                {errors.address && (
                  <p className="mt-1 text-sm text-red-400">{errors.address}</p>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Ville *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-black/30 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-transparent transition-all ${
                      errors.city ? 'border-red-500/50 bg-red-500/5' : 'border-white/[0.07]'
                    }`}
                    placeholder="Paris"
                  />
                  {errors.city && (
                    <p className="mt-1 text-sm text-red-400">{errors.city}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Code postal *
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-black/30 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-transparent transition-all ${
                      errors.postalCode ? 'border-red-500/50 bg-red-500/5' : 'border-white/[0.07]'
                    }`}
                    placeholder="75001"
                  />
                  {errors.postalCode && (
                    <p className="mt-1 text-sm text-red-400">{errors.postalCode}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Password */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white border-b border-white/[0.07] pb-2">
                Sécurité
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Mot de passe *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-12 py-3 bg-black/30 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-transparent transition-all ${
                      errors.password ? 'border-red-500/50 bg-red-500/5' : 'border-white/[0.07]'
                    }`}
                    placeholder="Choisir un mot de passe sécurisé"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-400">{errors.password}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Au moins 8 caractères avec une minuscule, une majuscule, un chiffre et un caractère spécial (!@#$%^&*...)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Confirmer le mot de passe *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-12 py-3 bg-black/30 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-transparent transition-all ${
                      errors.confirmPassword ? 'border-red-500/50 bg-red-500/5' : 'border-white/[0.07]'
                    }`}
                    placeholder="Confirmer votre mot de passe"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-400">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            {/* Terms */}
            <div>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleChange}
                  className="mt-1 w-5 h-5 text-yellow-600 border-white/[0.07] bg-black/30 rounded focus:ring-yellow-300 focus:ring-2"
                />
                <span className="text-sm text-gray-300">
                  J'accepte les{' '}
                  <button
                    type="button"
                    className="transition-colors underline"
                    style={{ color: theme.colors.primary.DEFAULT }}
                    onMouseEnter={(e) => e.target.style.color = theme.colors.primary.light}
                    onMouseLeave={(e) => e.target.style.color = theme.colors.primary.DEFAULT}
                  >
                    conditions d'utilisation
                  </button>{' '}
                  et la{' '}
                  <button
                    type="button"
                    className="transition-colors underline"
                    style={{ color: theme.colors.primary.DEFAULT }}
                    onMouseEnter={(e) => e.target.style.color = theme.colors.primary.light}
                    onMouseLeave={(e) => e.target.style.color = theme.colors.primary.DEFAULT}
                  >
                    politique de confidentialité
                  </button>
                </span>
              </label>
              {errors.acceptTerms && (
                <p className="mt-1 text-sm text-red-400">{errors.acceptTerms}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full font-semibold py-3 rounded-lg transition-all duration-200 disabled:bg-white/10 disabled:cursor-not-allowed"
              style={{
                backgroundColor: isLoading ? undefined : theme.colors.primary.DEFAULT,
                color: isLoading ? undefined : '#000000'
              }}
              onMouseEnter={(e) => !isLoading && (e.target.style.backgroundColor = theme.colors.primary.hover)}
              onMouseLeave={(e) => !isLoading && (e.target.style.backgroundColor = theme.colors.primary.DEFAULT)}
            >
              {isLoading ? 'Création du compte...' : 'Créer mon compte'}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-8 text-center">
            <p className="text-gray-400">
              Déjà un compte ?{' '}
              <button
                onClick={() => onNavigate('login')}
                className="font-medium transition-colors"
                style={{ color: theme.colors.primary.DEFAULT }}
                onMouseEnter={(e) => e.target.style.color = theme.colors.primary.light}
                onMouseLeave={(e) => e.target.style.color = theme.colors.primary.DEFAULT}
              >
                Se connecter
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;